import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Grid, Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

const ExhibitorGrid = forwardRef(({ tierExhibitors, viewMode }, ref) => {
  const [cardStates, setCardStates] = useState({});
  const [animatedCard, setAnimatedCard] = useState(null);
  const refMap = useRef({});

  // Sync cards with global viewMode
  useEffect(() => {
    const updated = {};
    tierExhibitors.forEach((ex) => {
      updated[ex._id] = viewMode === "boothPhoto" ? "boothPhoto" : "logo";
    });
    setCardStates(updated);
  }, [viewMode, tierExhibitors]);

  // Create refs for each exhibitor
  useEffect(() => {
    const map = {};
    tierExhibitors.forEach((ex) => {
      map[ex._id] = React.createRef();
    });
    refMap.current = map;
  }, [tierExhibitors]);

  // Card click handler for local flip cycle
  const handleCardClick = (id) => {
    setCardStates((prev) => {
      const current = prev[id] || "logo";
      const next =
        current === "logo"
          ? "boothPhoto"
          : current === "boothPhoto"
            ? "boothNumber"
            : "logo";
      return { ...prev, [id]: next };
    });
  };

  // Scroll to specific card and animate
  const scrollToExhibitor = (id) => {
    const ref = refMap.current[id];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

      const scrollDelay = setTimeout(() => {
        setAnimatedCard(id);
        const clearAnim = setTimeout(() => setAnimatedCard(null), 800);
        return () => clearTimeout(clearAnim);
      }, 600); // Delay for scroll duration

      return () => clearTimeout(scrollDelay);
    }
  };


  // Expose scrollToExhibitor to parent
  useImperativeHandle(ref, () => ({
    scrollToExhibitor,
  }));

  return (
    <Grid container spacing={2} justifyContent="center">
      {tierExhibitors.map((exhibitor) => {
        const state = cardStates[exhibitor._id] || "logo";
        const isScanned = exhibitor.scanned;
        const logoUrl = exhibitor?.logo?.image?.[0]?.url;
        const boothUrl = exhibitor?.boothPhoto?.[0]?.image?.[0]?.url;

        const isFlipped = state !== "logo";
        const showBoothNumber = state === "boothNumber";

        return (
          <Grid item xs={6} sm={4} md={3} key={exhibitor._id}>
            <Box
              onClick={() => handleCardClick(exhibitor._id)}
              ref={refMap.current[exhibitor._id]}
              sx={{
                "@keyframes shakeScale": {
                  "0%": { transform: "scale(1) rotate(0deg)" },
                  "20%": { transform: "scale(1.1) rotate(-2deg)" },
                  "40%": { transform: "scale(1.1) rotate(2deg)" },
                  "60%": { transform: "scale(1.1) rotate(-1deg)" },
                  "80%": { transform: "scale(1.1) rotate(1deg)" },
                  "100%": { transform: "scale(1) rotate(0deg)" },
                },
                animation:
                  animatedCard === exhibitor._id
                    ? "shakeScale 0.6s ease-in-out"
                    : "none",
                perspective: "1000px",
                width: "100%",
                aspectRatio: "1 / 1",
                cursor: "pointer",
                zIndex: animatedCard === exhibitor._id ? 10 : "auto",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  transition: "transform 0.6s",
                  transformStyle: "preserve-3d",
                  transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
              >
                {/* Front - Logo */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backfaceVisibility: "hidden",
                    border: isScanned
                      ? "2px solid #4CAF50"
                      : "1px solid lightgray",
                    borderRadius: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    filter: isScanned ? "none" : "grayscale(100%)",
                    backgroundColor: "#fff",
                  }}
                >
                  <img
                    src={logoUrl}
                    alt={exhibitor.brandName}
                    style={{
                      width: "80%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                  {isScanned && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        borderRadius: "50%",
                        width: 30,
                        height: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 24, color: "#4CAF50" }} />
                    </Box>
                  )}
                </Box>

                {/* Back - boothPhoto or boothNumber */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                    border: isScanned
                      ? "2px solid #4CAF50"
                      : "1px solid lightgray",
                    borderRadius: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    filter: isScanned ? "none" : "grayscale(100%)",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      position: "relative",
                      transition: "transform 0.6s",
                      transformStyle: "preserve-3d",
                      transform: showBoothNumber
                        ? "rotateY(180deg)"
                        : "rotateY(0deg)",
                    }}
                  >
                    {/* Booth Photo Face */}
                    <Box
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                      }}
                    >
                      <img
                        src={boothUrl}
                        alt={`${exhibitor.brandName} booth`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    {/* Booth Number Face */}
                    <Box
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Typography variant="p" textAlign="center">
                        Booth #
                      </Typography>
                      <Typography variant="h2" fontWeight="bold" textAlign="center">
                        {exhibitor.boothNumber}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Check icon stays consistent */}
                  {isScanned && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        borderRadius: "50%",
                        width: 30,
                        height: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 24, color: "#4CAF50" }} />
                    </Box>
                  )}
                </Box>

                {/* Booth Number Badge */}
                {!isFlipped && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 5,
                      color: "black",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      px: 1,
                      py: "2px",
                      borderRadius: "12px",
                    }}
                  >
                    {/* <Typography variant="p" textAlign="center" sx={{ fontSize: 10 }}>
                      Booth
                    </Typography> */}
                    <Typography variant="p" textAlign="center" sx={{ lineHeight: 1 }}>
                      Booth {exhibitor.boothNumber}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Name */}
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                fontWeight: "bold",
                textAlign: "center",
                color: isScanned ? "#4CAF50" : "inherit",
              }}
            >
              {exhibitor.brandName}
            </Typography>
          </Grid>
        );
      })}
    </Grid>
  );
});

export default ExhibitorGrid;
